"use client";

import { usePathname } from "next/navigation";
import { useEffect, useState } from "react";
import { useApolloClient, useMutation } from "@apollo/client";

import currentPatientIdSelector from "@/redux/slice/auth/selectors/currentPatientId";
import currentPatientSelector from "@/redux/slice/auth/selectors/currentPatient";
import currentAddressIdSelector from "@/redux/slice/location/selectors/currentAddressId";
import pharmaAddressDetailsSelector from "@/redux/slice/location/selectors/pharmaAddressDetails";
import { useAppSelector } from "@/redux/hooks";

import { setUserActionPayload } from "@/redux/slice/cart/slice";
import signedInStatusSelector from "@/redux/slice/auth/selectors/signedInState";
import { PharmaAddressDetails } from "@/redux/slice/location/interfaces/PharmaAddressDetails";
import userActionPayloadSelector from "@/redux/slice/cart/selectors/userActionPayload";
import authTokenSelector from "@/redux/slice/auth/selectors/authToken";

import { fetchServerCart } from "@/redux/slice/cart/utils/fetchServerCart";
import { syncCart } from "@/redux/slice/cart/utils/syncCart";
import { saveCartV2, saveCartV2Variables } from "@/components/graphql/types/saveCartV2";
import { SAVE_CART_V2 } from "@/components/graphql/serverCartV2";
import { SignedinState } from "@/redux/slice/auth/enums/SignedInState";
import { CartInputDataV2, GuestCartInputDataV2 } from "@/components/graphql/types/globalTypes";
import { saveServerCart } from "@/redux/slice/cart/utils/saveServerCart";
import useMobileDetect from "@/libs/useMobileDetect";
import guestUserActionPayloadSelector from "../selectors/guestUserActionPayload";

declare global {
  interface Window {
    ReactNativeWebView: any;
  }
}

const useManageServerCart = ({ dispatch }) => {
  // router and network hooks
  const apolloClient = useApolloClient();
  const currentDevice = useMobileDetect();
  const isWebView = currentDevice.isWebView();
  const routerPathName = usePathname() as string;

  const [isCartFetchingUpdatingRequired, setIsCartFetchingUpdatingRequired] = useState(true);

  // selectors
  const currentPatientId = useAppSelector(currentPatientIdSelector);
  const currentPatient = useAppSelector(currentPatientSelector);
  const isSignedIn = useAppSelector(signedInStatusSelector);
  const userActionPayload = useAppSelector(userActionPayloadSelector);
  const pharmaAddressDetails = useAppSelector(pharmaAddressDetailsSelector);
  const deliveryAddressId = useAppSelector(currentAddressIdSelector);
  const guestUserActionPayload = useAppSelector(guestUserActionPayloadSelector);
  const authToken = useAppSelector(authTokenSelector);

  const [saveCartMutation] = useMutation<saveCartV2, saveCartV2Variables>(SAVE_CART_V2);

  const { id: patientId = "" } = currentPatient || {};

  useEffect(() => {
    if (pharmaAddressDetails?.pincode) {
      localStorage.setItem("pharmaPincode", pharmaAddressDetails.pincode);
    }
  }, [pharmaAddressDetails]);

  useEffect(() => {
    localStorage.setItem("deliveryAddressId", deliveryAddressId);
  }, [deliveryAddressId]);

  useEffect(() => {
    if (isSignedIn === SignedinState.SIGNEDOUT) syncCart(dispatch);
    // this is for transition from signed out to signed in

    if (isSignedIn === SignedinState.SIGNEDIN) {

      const pharmaAddressDetailsFromLocalStorage: PharmaAddressDetails =
        localStorage.getItem("pharmaAddressDetails") && JSON.parse(localStorage.getItem("pharmaAddressDetails"));
      localStorage.removeItem("pharmaAddressDetails");
      // create cart payload
      const cartInputPayload: Partial<CartInputDataV2> = {
        ...(!(isWebView && window?.ReactNativeWebView) &&
          pharmaAddressDetailsFromLocalStorage && {
            address: {
              zipcode: pharmaAddressDetailsFromLocalStorage?.pincode,
              ...(pharmaAddressDetailsFromLocalStorage?.lat &&
                pharmaAddressDetailsFromLocalStorage?.lng && {
                  coordinate: {
                    latitude: Number(pharmaAddressDetailsFromLocalStorage?.lat),
                    longitude: Number(pharmaAddressDetailsFromLocalStorage?.lng),
                  },
                }),
            },
          }),
      };

      if (Object.keys(cartInputPayload)?.length) {
        dispatch(setUserActionPayload(cartInputPayload));
      } else {
        setIsCartFetchingUpdatingRequired(false);
        fetchServerCart(
          {
            patientId,
            apolloClient,
            authToken,
          },
          dispatch
        );
      }
    }
  }, [isSignedIn, routerPathName]);

  useEffect(() => {
    if (isSignedIn === SignedinState.SIGNEDIN && userActionPayload) {
      const cartInputDataV2: CartInputDataV2 = {
        ...userActionPayload,
        patientId: currentPatientId,
      };
      saveServerCart({
        authToken,
        cartInputDataV2,
        dispatch,
        saveCartMutation,
        setIsCartFetchingUpdatingRequired,
        isCartFetchingUpdatingRequired,
      });
    }
  }, [userActionPayload]);
  useEffect(() => {
    // Save cart for logged out user
    if (isSignedIn === SignedinState.SIGNEDOUT && guestUserActionPayload) {
      const guestCartInputDataV2: GuestCartInputDataV2 = {
        ...guestUserActionPayload,
        id: (localStorage?.getItem("guestCartId") as string) || "",
      };
      saveServerCart({
        authToken,
        guestCartInputDataV2,
        dispatch,
        setIsCartFetchingUpdatingRequired,
        isCartFetchingUpdatingRequired,
      });
    }
  }, [guestUserActionPayload]);
};

export default useManageServerCart;
